import { z } from 'zod';

/** Contratos de Transações + recorrência (M3). Valores em centavos; datas date-only. */

export const transactionTypeSchema = z.enum(['income', 'expense', 'transfer']);
export type TransactionType = z.infer<typeof transactionTypeSchema>;

export const transactionKindSchema = z.enum(['standard', 'card_payment', 'adjustment']);
export type TransactionKind = z.infer<typeof transactionKindSchema>;

const ymdSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD.');
const ymSchema = z.string().regex(/^\d{4}-\d{2}$/, 'Mês deve ser YYYY-MM.');

/** Tag reduzida embutida na linha (id + nome). */
export const txTagSchema = z.object({ id: z.string(), name: z.string() });

/** Linha de transação como devolvida pela listagem (com joins resolvidos). */
export const transactionSchema = z.object({
  id: z.string(),
  type: transactionTypeSchema,
  kind: transactionKindSchema,
  date: ymdSchema,
  description: z.string(),
  /** Com sinal: despesa < 0, receita > 0. */
  amountCents: z.number().int(),
  effectuated: z.boolean(),
  ignoreInReports: z.boolean(),
  accountId: z.string().nullable(),
  accountName: z.string().nullable(),
  /** Transferência lógica: origem/destino resolvidas; null em lançamentos comuns ou legados sem vínculo. */
  fromAccountId: z.string().nullable(),
  fromAccountName: z.string().nullable(),
  toAccountId: z.string().nullable(),
  toAccountName: z.string().nullable(),
  cardId: z.string().nullable(),
  cardName: z.string().nullable(),
  invoiceMonth: ymSchema.nullable(),
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  categoryColor: z.string().nullable(),
  categoryIcon: z.string().nullable(),
  categoryIsSystem: z.boolean(),
  transferGroupId: z.string().nullable(),
  recurrenceId: z.string().nullable(),
  installmentIndex: z.number().int().nullable(),
  installmentTotal: z.number().int().nullable(),
  note: z.string().nullable(),
  tags: z.array(txTagSchema),
});
export type Transaction = z.infer<typeof transactionSchema>;

export const transactionsSummarySchema = z.object({
  incomeCents: z.number().int(),
  expenseCents: z.number().int(),
  balanceCents: z.number().int(),
});
export type TransactionsSummary = z.infer<typeof transactionsSummarySchema>;

export const transactionsResponseSchema = z.object({
  rows: z.array(transactionSchema),
  summary: transactionsSummarySchema,
});
export type TransactionsResponse = z.infer<typeof transactionsResponseSchema>;

// --- Query de listagem/filtros ---

export const transactionsQuerySchema = z.object({
  month: ymSchema.optional(),
  from: ymdSchema.optional(),
  to: ymdSchema.optional(),
  type: transactionTypeSchema.optional(),
  accountId: z.string().optional(),
  /** CSV de contas para seleção múltipla processada no servidor. */
  accountIds: z.string().optional(),
  /** Projeção sem linhas detalhadas para consumidores que precisam só dos agregados. */
  view: z.enum(['full', 'summary']).optional(),
  cardId: z.string().optional(),
  /** CSV de ids no querystring; o service divide por vírgula. */
  categoryIds: z.string().optional(),
  tagIds: z.string().optional(),
  status: z.enum(['effected', 'pending']).optional(),
}).strict();
export const transactionsSearchBodySchema = transactionsQuerySchema.extend({
  /** Texto potencialmente sensível: somente em body POST, nunca em query string. */
  search: z.string().trim().min(1).max(200).optional(),
});
export type TransactionsQuery = z.infer<typeof transactionsSearchBodySchema>;

// --- Recorrência ---

/** Recorrência anexada a um lançamento novo. */
export const recurrenceInputSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('fixed') }),
  z.object({
    mode: z.literal('installment'),
    times: z.number().int().min(2).max(120),
  }),
  z.object({
    mode: z.literal('repeat'),
    times: z.number().int().min(2).max(120),
    frequency: z.enum(['monthly']).default('monthly'),
  }),
]);
export type RecurrenceInput = z.infer<typeof recurrenceInputSchema>;

/** Escopo de edição/exclusão de séries recorrentes. */
export const recurrenceScopeSchema = z.enum(['one', 'future', 'all']);
export type RecurrenceScope = z.infer<typeof recurrenceScopeSchema>;

// --- Criação (união discriminada por tipo) ---

const baseEntry = {
  date: ymdSchema,
  description: z.string().trim().max(120).default(''),
  effectuated: z.boolean().default(true),
  ignoreInReports: z.boolean().default(false),
  note: z.string().trim().max(500).optional(),
  tagIds: z.array(z.string()).optional(),
};

/** Despesa/Receita: exige categoria + (conta OU cartão). amountCents positivo. */
export const createEntryBodySchema = z.object({
  type: z.enum(['income', 'expense']),
  amountCents: z.number().int().positive('O valor deve ser maior que zero.'),
  categoryId: z.string().min(1, 'Escolha uma categoria.'),
  accountId: z.string().optional(),
  cardId: z.string().optional(),
  recurrence: recurrenceInputSchema.optional(),
  ...baseEntry,
});
export type CreateEntryBody = z.infer<typeof createEntryBodySchema>;

/** Transferência: par débito/crédito entre contas, sem categoria. */
export const createTransferBodySchema = z.object({
  type: z.literal('transfer'),
  amountCents: z.number().int().positive('O valor deve ser maior que zero.'),
  fromAccountId: z.string().min(1, 'Escolha a conta de origem.'),
  toAccountId: z.string().min(1, 'Escolha a conta de destino.'),
  date: ymdSchema,
  description: z.string().trim().max(120).default(''),
  effectuated: z.boolean().default(true),
  note: z.string().trim().max(500).optional(),
});
export type CreateTransferBody = z.infer<typeof createTransferBodySchema>;

export const createTransactionBodySchema = z.union([
  createEntryBodySchema,
  createTransferBodySchema,
]);
export type CreateTransactionBody = z.infer<typeof createTransactionBodySchema>;

/** PATCH: campos editáveis; o escopo vai no querystring (?scope=). */
export const updateTransactionBodySchema = z.object({
  /** Valor da ocorrência editada. Em scope future/all, torna-se o valor de cada ocorrência afetada. */
  amountCents: z.number().int().positive().optional(),
  date: ymdSchema.optional(),
  description: z.string().trim().max(120).optional(),
  categoryId: z.string().optional(),
  accountId: z.string().optional(),
  cardId: z.string().optional(),
  fromAccountId: z.string().min(1, 'Escolha a conta de origem.').optional(),
  toAccountId: z.string().min(1, 'Escolha a conta de destino.').optional(),
  effectuated: z.boolean().optional(),
  ignoreInReports: z.boolean().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  tagIds: z.array(z.string()).optional(),
});
export type UpdateTransactionBody = z.infer<typeof updateTransactionBodySchema>;

export const transactionResponseSchema = z.object({ transaction: transactionSchema });
export type TransactionResponse = z.infer<typeof transactionResponseSchema>;
